// CLASELE editorului: tipul de secțiune al fiecărui element, derivat din
// geometrie — niciodată memorat.
//
// Platforma numără «clase» în două feluri (`structure/clase.py`: clasa de
// execuție, cu armarea; `geometrie/orbite.py`: orbita de simetrie, prin
// poziție). În editor nu există armare, deci clasa de execuție se reduce la
// partea ei geometrică: aceeași familie, aceeași secțiune la nivelul de pe
// ecran, aceeași formă (I / L / T) cu aceleași brațe — un tip de cofraj.
// E ce numără deja «Tipuri ESV folosite» (D8), pe un singur nivel.
//
// Clasa e o CHEIE calculată pe loc: un element schimbat își schimbă cheia și
// e, automat, în altă clasă — fără apartenență scrisă, fără «scoatere din
// clasă». Numele clasei e secțiunea ei («30×140 L»), nu o literă: SA/SB ale
// Pasului 19 se renumerotează după populație la fiecare rulare, pe când
// secțiunea rămâne aceeași cât n-o schimbă omul. Scara de 5 cm a lui
// `clase.py` nu se aplică: în editor cotele le scrie omul, iar 30×140 și
// 30×142 sunt două decizii ale lui, care trebuie să se vadă ca două clase.

import { sectiunea, type Brat, type Esv, type Geometrie, type Grinda } from "./editorGeometrie";
import { numeleFormei, redimBrat, stergeBrat } from "./editorForme";
import { redimEsv } from "./editorGeometrie";

export type Familie = "esv" | "grinda";
export type Clasa = {
  familie: Familie;
  cheie: string;
  /** «30×140 L», «25×50» — ce scrie și pe eticheta elementului */
  eticheta: string;
  /** id-urile membrilor, în ordinea din geometrie */
  membri: string[];
};

const nr = (v: number) => String(Math.round(v * 10) / 10);

/** Cheia clasei unui ESV la `nivel`: secțiunea piciorului, forma și brațele
 *  (grosime × lungime, ca multiset — un L întors e același cofraj), sau
 *  null când elementul nu există pe nivel. */
export function cheiaEsv(e: Esv, nivel: number): string | null {
  const s = sectiunea(e, nivel);
  if (!s) return null;
  const brate = (s.brate ?? []).map((b) => `${nr(b.b_cm)}x${nr(b.L_cm)}`).sort();
  const forma = brate.length ? numeleFormei(e, nivel) : "I";
  return `esv|${nr(s.b_cm)}x${nr(s.L_cm)}|${forma}${brate.length ? "|" + brate.join(",") : ""}`;
}

/** Cheia clasei unei grinzi la `nivel`: lățimea × înălțimea, sau null. */
export function cheiaGrinzii(g: Grinda, nivel: number): string | null {
  const s = g.sectiune_per_nivel[String(nivel)];
  if (!s) return null;
  return `grinda|${nr(s.b_cm)}x${nr(Math.round(s.h_m * 100))}`;
}

/** Eticheta omului pentru o cheie: «30×140», «30×140 L», «30×140 compusă». */
export function etichetaClasei(cheie: string, t: (k: string) => string): string {
  const [, sec, forma] = cheie.split("|");
  const dim = sec.replace("x", "×");
  if (!forma || forma === "I") return dim;
  return `${dim} ${forma === "compusa" ? t("struct.editor.forma_compusa") : forma}`;
}

/** Clasele nivelului, pe familii, de la cea mai populată (ca `clase.py`),
 *  la egalitate după etichetă. */
export function clasele(G: Geometrie, nivel: number, t: (k: string) => string): Clasa[] {
  const grupe = new Map<string, Clasa>();
  const pune = (familie: Familie, cheie: string | null, id: string) => {
    if (!cheie) return;
    let c = grupe.get(cheie);
    if (!c) {
      c = { familie, cheie, eticheta: etichetaClasei(cheie, t), membri: [] };
      grupe.set(cheie, c);
    }
    c.membri.push(id);
  };
  for (const e of G.esv) pune("esv", cheiaEsv(e, nivel), e.id);
  for (const g of G.grinzi) pune("grinda", cheiaGrinzii(g, nivel), g.id);
  return [...grupe.values()].sort((a, b) =>
    a.familie !== b.familie ? (a.familie === "esv" ? -1 : 1)
      : b.membri.length - a.membri.length || a.eticheta.localeCompare(b.eticheta, "ro", { numeric: true }));
}

/** Cheia clasei elementului `id` (ESV sau grindă) la `nivel`. */
export function cheiaLui(G: Geometrie, id: string, nivel: number): string | null {
  const e = G.esv.find((x) => x.id === id);
  if (e) return cheiaEsv(e, nivel);
  const g = G.grinzi.find((x) => x.id === id);
  return g ? cheiaGrinzii(g, nivel) : null;
}

/** Membrii clasei lui `id` la `nivel`, cu el cu tot; `[id]` când e singur. */
export function membrii(G: Geometrie, id: string, nivel: number): string[] {
  const cheie = cheiaLui(G, id, nivel);
  if (!cheie) return [id];
  const fam = cheie.split("|")[0];
  return fam === "esv"
    ? G.esv.filter((e) => cheiaEsv(e, nivel) === cheie).map((e) => e.id)
    : G.grinzi.filter((g) => cheiaGrinzii(g, nivel) === cheie).map((g) => g.id);
}

/** Aceeași operație pe fiecare din `ids`, la rând: «Toată clasa» e un singur
 *  pas de anulare, iar grinzile se așază o dată, după toate. */
export function peMembri(G: Geometrie, ids: Iterable<string>,
                         op: (G: Geometrie, id: string) => Geometrie): Geometrie {
  let out = G;
  for (const id of ids) out = op(out, id);
  return out;
}

/** Brațul membrului `e` care e «același» cu `ref` — aceeași grosime și
 *  lungime —, ca o schimbare pe clasă să-l găsească oriunde ar sta pe picior
 *  (la membrii unei clase brațele sunt la fel ca multiset, nu ca index). */
export function indexBratului(e: Esv, nivel: number, ref: Brat): number | null {
  const brate = sectiunea(e, nivel)?.brate ?? [];
  const i = brate.findIndex((b) => Math.abs(b.b_cm - ref.b_cm) < 0.11 && Math.abs(b.L_cm - ref.L_cm) < 0.11);
  return i < 0 ? null : i;
}

/** Membrii `altii` urmează ce s-a schimbat la secțiunea lui `id` între `G0`
 *  și `G1` (o tragere de capăt, în curs sau terminată): latura lungă, lungimea
 *  unui braț, un braț scos. Membrii se iau din `G0` — în timpul tragerii
 *  elementul tras e deja în altă clasă, deci clasa lui de ACUM n-are pe
 *  nimeni. `nivel` = numai nivelul ăsta, null = toate (regula lui `redimEsv`). */
export function urmeazaClasa(G0: Geometrie, G1: Geometrie, id: string, nivelRef: number,
                             nivel: number | null, altii: readonly string[]): Geometrie {
  if (!altii.length) return G1;
  const e0 = G0.esv.find((x) => x.id === id);
  const e1 = G1.esv.find((x) => x.id === id);
  const s0 = e0 && sectiunea(e0, nivelRef);
  const s1 = e1 && sectiunea(e1, nivelRef);
  if (!s0 || !s1) return G1;
  let G2 = G1;
  if (Math.abs(s1.L_cm - s0.L_cm) > 0.05)
    G2 = peMembri(G2, altii, (g, m) => redimEsv(g, m, nivel, { L_cm: s1.L_cm }));
  for (const br0 of s0.brate ?? []) {
    const br1 = (s1.brate ?? []).find((x) => Math.abs(x.la_mm - br0.la_mm) <= 1);
    if (br1 && Math.abs(br1.L_cm - br0.L_cm) < 0.05) continue;
    G2 = peMembri(G2, altii, (g, m) => {
      const e = g.esv.find((x) => x.id === m);
      const i = e ? indexBratului(e, nivelRef, br0) : null;
      if (i == null) return g;
      return br1 ? redimBrat(g, m, nivelRef, nivel, i, { L_cm: br1.L_cm })
        : stergeBrat(g, m, nivelRef, nivel, i);
    });
  }
  return G2;
}
